// RecordCard (F-185, docs/PAGE-GRAMMAR.md): the one card anatomy of a record screen, in four variants, over the
// design system's Card (#7). Every card reads in the same order: what it is (icon · title), where it stands (the status
// pill), who owns it and when it is due (its rows), what you can do (the footer's one action, the ⋯ menu).
//
//   summary  header icon · title · pill · ⋯ menu; body up to five label/value rows; footer: the time left, one action right
//   list     up to five rows, then *All {what}* to the screen that holds every one
//   form     the host's fields (an existing part form, a scorecard page)
//   stepper  numbered steps in ONE card (the intake's Part 0 · Part 1 · the call · Part 2), each with owner and state
//
// Controlled and data-free; a row past the fifth is never drawn here (the list's link reaches it).

import type { ReactNode } from "react";
import { Card, type CardProps } from "../core/Card";
import { EmptyState } from "../core/EmptyState";
import { Icon } from "../core/icons";
import { KeyValue, type KeyValueItem } from "../core/KeyValue";
import { TextLink } from "../core/Link";
import { MenuButton, type MenuItem } from "../core/Menu";
import { cx } from "../lib/cx";
import { G9_LABELS, G9_TEMPLATES } from "./labels";

/** At most this many rows on a summary or list card. */
export const CARD_ROWS = 5;

export interface RecordListRow {
  key: string;
  /** What the row is (a name, a link). */
  primary: ReactNode;
  /** Where it stands, who owns it, when it is due. */
  secondary?: ReactNode | undefined;
  /** At the row's end: a pill, a count, an action. */
  trailing?: ReactNode | undefined;
}

export interface RecordStep {
  key: string;
  label: string;
  /** Who owns the step (a role word or a name). */
  owner?: ReactNode | undefined;
  /** Where it stands (a pill, or the words it waits on). */
  state?: ReactNode | undefined;
  href?: string | undefined;
  /** The step the screen shows now (aria-current="step"). */
  current?: boolean | undefined;
  done?: boolean | undefined;
}

interface Base extends Pick<CardProps, "title" | "icon" | "pills" | "sub" | "count" | "loading" | "empty" | "error" | "refused" | "tone" | "can" | "hs" | "id"> {
  /** Columns the card spans in the grid: 1 (default), 2, or the whole row. */
  span?: 1 | 2 | "all" | undefined;
  /** The ⋯ menu's items (the card's other acts). */
  menu?: readonly MenuItem[] | undefined;
  /** The footer: the time on the left (a formatted instant), one action on the right. */
  at?: ReactNode | undefined;
  action?: ReactNode | undefined;
  className?: string | undefined;
}

export type RecordCardProps =
  | (Base & { variant: "summary"; rows: readonly KeyValueItem[]; children?: ReactNode })
  | (Base & { variant: "list"; rows: readonly RecordListRow[]; all?: { href: string; what: string; always?: boolean | undefined } | undefined; children?: ReactNode })
  | (Base & { variant: "form"; children?: ReactNode })
  | (Base & { variant: "stepper"; steps: readonly RecordStep[]; label: string; children?: ReactNode });

function body(props: RecordCardProps): ReactNode {
  switch (props.variant) {
    case "summary":
      return props.rows.length ? <KeyValue rows items={props.rows.slice(0, CARD_ROWS)} /> : null;
    case "list": {
      const shown = props.rows.slice(0, CARD_ROWS);
      return shown.length ? (
        <ul className="mc-rc-list">
          {shown.map((row) => (
            <li key={row.key} className="mc-rc-row">
              <span className="mc-rc-main">
                <span className="mc-rc-p">{row.primary}</span>
                {row.secondary !== undefined ? <span className="mc-rc-s">{row.secondary}</span> : null}
              </span>
              {row.trailing !== undefined ? <span className="mc-rc-t">{row.trailing}</span> : null}
            </li>
          ))}
        </ul>
      ) : null;
    }
    case "stepper":
      return (
        <ol className="mc-rc-steps" aria-label={props.label}>
          {props.steps.map((step, at) => {
            const inner = (
              <>
                <span className={cx("mc-rc-n", step.done && "mc-rc-n-done")} aria-hidden="true">{step.done ? <Icon name="check" size={14} /> : at + 1}</span>
                <span className="mc-rc-main">
                  <span className="mc-rc-p">{step.label}</span>
                  {step.owner !== undefined ? <span className="mc-rc-s">{step.owner}</span> : null}
                </span>
                {step.state !== undefined ? <span className="mc-rc-t">{step.state}</span> : null}
              </>
            );
            return (
              <li key={step.key} className={cx("mc-rc-step", step.current && "mc-on")}>
                {step.href
                  ? <a className="mc-rc-step-a" href={step.href} aria-current={step.current ? "step" : undefined}>{inner}</a>
                  : <div className="mc-rc-step-a" aria-current={step.current ? "step" : undefined}>{inner}</div>}
              </li>
            );
          })}
        </ol>
      );
    default:
      return null;
  }
}

export function RecordCard(props: RecordCardProps) {
  const { title, icon, pills, sub, count, loading, empty, error, refused, tone, can, hs, id, span, menu, at, action, className, children } = props;
  const all = props.variant === "list" && props.all && (props.all.always || props.rows.length > CARD_ROWS) ? props.all : undefined;
  const menuButton = menu && menu.some((item) => item.can !== false)
    ? <MenuButton label={G9_LABELS.actions} items={menu} iconOnly sm align="end" />
    : undefined;
  const footAction = action ?? (all ? <TextLink after="fwd" href={all.href}>{G9_TEMPLATES.allOf({ what: all.what })}</TextLink> : undefined);
  return (
    <Card
      title={title}
      icon={icon}
      pills={pills}
      sub={sub}
      count={count}
      loading={loading}
      error={error}
      refused={refused}
      tone={tone}
      can={can}
      hs={hs}
      id={id}
      actions={menuButton}
      className={cx("mc-rc", `mc-rcv-${props.variant}`, span === 2 && "mc-cg-2", span === "all" && "mc-cg-all", className)}
    >
      {empty !== undefined && empty !== null && empty !== false
        ? (typeof empty === "string" ? <EmptyState>{empty}</EmptyState> : empty)
        : <>{body(props)}{children ?? null}</>}
      {at !== undefined || footAction !== undefined ? (
        <div className="mc-rc-f">
          {at !== undefined ? <span className="mc-rc-at">{at}</span> : <span />}
          {footAction ?? null}
        </div>
      ) : null}
    </Card>
  );
}
